import React, { useMemo, useState } from 'react';
import { LauncherLayout } from "../layout/LauncherLayout";
import {
    Card, Row, Col, Typography, Spin, Tag, Space, Button, Flex, Divider,
    Avatar, Timeline, Alert, Image, Breadcrumb, Progress
} from "antd";
import {
    BookOutlined, UserOutlined, CalendarOutlined, GlobalOutlined,
    EnvironmentOutlined, WarningOutlined, DollarOutlined, TeamOutlined,
    ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined,
    SafetyCertificateOutlined, HomeOutlined, ReadOutlined, EyeOutlined, ClockCircleOutlined
} from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PublicInvitationDetailDto, JoinRequestDto } from "../../commons/data/types";
import dayjs from "dayjs";
import { useAuthContext } from "../auth/LauncherAuthContext";
import { ReservationCtaSection } from "../../reservation/ReservationCtaSection";
import { SessionRsvpBookingForm } from "./SessionRsvpBookingForm";
import { Label } from "../../commons/translations/Label";

const { Title, Paragraph, Text } = Typography;

export const InvitationDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showBookingForm, setShowBookingForm] = useState<boolean>(false);

    const { user, isUserLoggedIn } = useAuthContext();

    const { data: detail, isLoading, isError } = useQuery<PublicInvitationDetailDto>(
        ['public-invitation-detail', id],
        () => axios.get(`/api/public/invitations/${id}`).then(res => res.data),
        { enabled: !!id }
    );

    const { data: userJoinRequest, refetch: refetchUserJoinRequest } = useQuery<JoinRequestDto | null>(
        ['my-join-request-for-invitation', id, isUserLoggedIn()],
        () => axios.get(`/api/launcher/my/join-requests/invitation/${id}`).then(res => res.data).catch(() => null),
        { enabled: isUserLoggedIn() && !!id }
    );

    const hasUserAlreadyReserved = useMemo<boolean>(() => {
        return !!userJoinRequest && userJoinRequest.status !== 'CANCELED';
    }, [userJoinRequest]);

    const isOrganizer = useMemo<boolean>(() => {
        if (!user || !detail) return false;
        return !!detail.organizerId && (user.organizer?.id === detail.organizerId || user.id === detail.organizerId);
    }, [user, detail]);

    const isGm = useMemo<boolean>(() => {
        if (!user || !detail) return false;
        return !!detail.gmId && user.id === detail.gmId;
    }, [user, detail]);

    const verifiedPlayers = useMemo<number>(() => {
        if (!detail) return 0;
        return Math.max(0, detail.maxTotalPlayers - detail.availableSeats);
    }, [detail]);

    const isOverMinimum = useMemo<boolean>(() => {
        if (!detail) return false;
        return verifiedPlayers >= detail.minTotalPlayers;
    }, [detail, verifiedPlayers]);

    const isAtMaximum = useMemo<boolean>(() => {
        if (!detail) return false;
        return verifiedPlayers >= detail.maxTotalPlayers;
    }, [detail, verifiedPlayers]);

    if (isLoading) {
        return (
            <LauncherLayout>
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin size="large" />
                </div>
            </LauncherLayout>
        );
    }

    if (isError || !detail) {
        return (
            <LauncherLayout>
                <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
                    <Alert
                        message={t('invitations.detail.error.title', 'Invitation Not Found')}
                        description={t('invitations.detail.error.desc', 'The requested adventure invitation could not be found or is no longer active.')}
                        type="error"
                        showIcon
                        action={
                            <Button type="primary" onClick={() => navigate('/invitations')}>
                                {t('invitations.backToBrowse', 'Back to Adventures')}
                            </Button>
                        }
                    />
                </div>
            </LauncherLayout>
        );
    }

    return (
        <LauncherLayout>
            <div style={{ maxWidth: '1100px', margin: '30px auto', padding: '0 20px' }}>
                {/* BREADCRUMB & BACK BUTTON */}
                <div style={{ marginBottom: 16 }}>
                    <Flex justify="space-between" align="center">
                        <Breadcrumb
                            items={[
                                { title: <Link to="/"><HomeOutlined /> {t('common.home', 'Home')}</Link> },
                                { title: <Link to="/invitations">{t('invitations.browse.nav', 'Adventures')}</Link> },
                                { title: detail.title }
                            ]}
                        />
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/invitations')}>
                            {t('invitations.backToBrowse', 'Back to List')}
                        </Button>
                    </Flex>
                </div>

                {/* HERO BANNER CARD */}
                <Card
                    style={{
                        ...launcherCard,
                        marginBottom: 24,
                        overflow: 'hidden',
                        padding: 0
                    }}
                >
                    <Row gutter={[0, 0]}>
                        <Col xs={24} md={10}>
                            <div style={{ height: '100%', minHeight: 280, position: 'relative', background: '#111' }}>
                                {detail.coverUrl ? (
                                    <img
                                        src={detail.coverUrl}
                                        alt={detail.title}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <BookOutlined style={{ fontSize: 64, color: '#555' }} />
                                    </div>
                                )}

                                <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {detail.online ? (
                                        <Tag color="cyan" icon={<GlobalOutlined />} style={{ fontSize: 13, padding: '4px 10px' }}>
                                            {detail.onlinePlatform ? `Online - ${detail.onlinePlatform}` : 'Online'}
                                        </Tag>
                                    ) : (
                                        <Tag color="geekblue" icon={<EnvironmentOutlined />} style={{ fontSize: 13, padding: '4px 10px' }}>
                                            {t('invitations.inPerson', 'In-Person')}
                                        </Tag>
                                    )}
                                </div>
                            </div>
                        </Col>

                        <Col xs={24} md={14} style={{ padding: 24, display: 'flex', flexDirection: 'column' }}>
                            <Title level={2} style={{ margin: '0 0 12px 0' }}>
                                {detail.title}
                            </Title>

                            {/* GAME SYSTEM & ORGANIZER */}
                            <Space size="large" wrap style={{ marginBottom: 16 }}>
                                {detail.systemTitle && (
                                    <Flex align="center" gap="small">
                                        {detail.systemMediaUrl && (
                                            <Image src={detail.systemMediaUrl} preview={false} width={20} height={20} style={{ borderRadius: 3, objectFit: 'cover' }} />
                                        )}
                                        <Text strong>{detail.systemTitle}</Text>
                                    </Flex>
                                )}

                                {detail.organizerName && (
                                    <Flex align="center" gap="small">
                                        {detail.organizerLogoUrl ? (
                                            <Avatar src={detail.organizerLogoUrl} size="small" />
                                        ) : (
                                            <ReadOutlined />
                                        )}
                                        <Text type="secondary">{detail.organizerName}</Text>
                                    </Flex>
                                )}
                            </Space>

                            {/* KEY METRICS GRID */}
                            <Row gutter={[16, 16]} style={{ marginTop: 'auto', background: '#fafafa', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}>
                                <Col xs={12} sm={6}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {t('invitations.availableSeats', 'Available Seats')}
                                    </Text>
                                    <Tag color="green" icon={<TeamOutlined />} style={{ fontSize: 14, fontWeight: 'bold', margin: '4px 0 0 0' }}>
                                        {detail.availableSeats} / {detail.maxTotalPlayers}
                                    </Tag>
                                </Col>

                                <Col xs={12} sm={6}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {t('invitations.costPerSession', 'Cost / Session')}
                                    </Text>
                                    <Text strong style={{ fontSize: 16, color: '#1890ff', margin: '2px 0 0 0', display: 'block' }}>
                                        <DollarOutlined /> {detail.costPerSession != null ? `${detail.costPerSession} €` : 'Free'}
                                    </Text>
                                </Col>

                                <Col xs={12} sm={6}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {t('invitations.sessions', 'Sessions')}
                                    </Text>
                                    <Text strong style={{ fontSize: 16, margin: '2px 0 0 0', display: 'block' }}>
                                        <CalendarOutlined /> {detail.sessions?.length || 0}
                                    </Text>
                                </Col>

                                <Col xs={12} sm={6}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {t('invitations.deadline', 'Deadline')}
                                    </Text>
                                    <Text strong style={{ fontSize: 13, margin: '4px 0 0 0', display: 'block' }}>
                                        {dayjs(detail.invitationDeadline).format('YYYY-MM-DD HH:mm')}
                                    </Text>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Card>

                {/* MAIN CONTENT DETAILS */}
                <Row gutter={[24, 24]}>
                    {/* LEFT COLUMN: Adventure Description & Content Warnings & Sessions */}
                    <Col xs={24} lg={16}>
                        {/* DESCRIPTION */}
                        <Card style={{ ...launcherCard, marginBottom: 24 }} title={<Title level={4} style={{ margin: 0 }}><BookOutlined /> {t('invitations.detail.description', 'Adventure Description')}</Title>}>
                            <Paragraph style={{ fontSize: 15, lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                                {detail.description}
                            </Paragraph>

                            {/* CONTENT WARNINGS */}
                            {detail.contentWarnings && detail.contentWarnings.length > 0 && (
                                <>
                                    <Divider style={{ margin: '16px 0' }} />
                                    <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                                        <WarningOutlined style={{ color: '#fa8c16' }} /> {t('organizer.adventures.form.contentWarnings', 'Content Warnings')}
                                    </Title>
                                    <Space wrap size={[0, 8]}>
                                        {detail.contentWarnings.map(cw => (
                                            <Tag key={cw.id} color="orange" icon={<WarningOutlined />} style={{ fontSize: 13, padding: '4px 10px' }}>
                                                {t(cw.key || cw.id)}
                                            </Tag>
                                        ))}
                                    </Space>
                                </>
                            )}
                        </Card>

                        {/* SESSIONS SCHEDULE */}
                        <Card style={{ ...launcherCard, marginBottom: 24 }} title={<Title level={4} style={{ margin: 0 }}><CalendarOutlined /> {t('invitations.detail.sessionsSchedule', 'Sessions Schedule')}</Title>}>
                            {detail.sessions && detail.sessions.length > 0 ? (
                                <Timeline
                                    style={{ marginTop: 16 }}
                                    items={detail.sessions.map((s, idx) => ({
                                        color: 'blue',
                                        children: (
                                            <div>
                                                <Text strong>{t('invitations.detail.sessionNum', 'Session')} #{idx + 1}: </Text>
                                                <Text>{dayjs(s.time).format('dddd, MMMM D, YYYY [at] HH:mm')}</Text>
                                            </div>
                                        )
                                    }))}
                                />
                            ) : (
                                <Paragraph type="secondary">
                                    {t('invitations.detail.noSessions', 'No specific session dates scheduled yet.')}
                                </Paragraph>
                            )}
                        </Card>

                        {/* CANCELLATION POLICY & APPROVAL */}
                        <Card style={{ ...launcherCard }} title={<Title level={4} style={{ margin: 0 }}><InfoCircleOutlined /> {t('invitations.detail.policies', 'Policies & Rules')}</Title>}>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                    {t('invitations.detail.cancelationPolicy', 'Cancellation Policy')}
                                </Text>
                                <Paragraph type="secondary" style={{ background: '#fafafa', padding: 12, borderRadius: 6, border: '1px dashed #d9d9d9' }}>
                                    {detail.cancelationPolicy || t('invitations.detail.noPolicy', 'Standard cancellation rules apply.')}
                                </Paragraph>
                            </div>

                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                    {t('invitations.detail.approvalMode', 'Player Approval')}
                                </Text>
                                {detail.approvalRequired ? (
                                    <Tag color="gold" icon={<SafetyCertificateOutlined />}>
                                        {t('invitations.detail.approvalRequired', 'GM Approval Required for Reservation')}
                                    </Tag>
                                ) : (
                                    <Tag color="green" icon={<CheckCircleOutlined />}>
                                        {t('invitations.detail.instantBooking', 'Instant Reservation (Auto-approved)')}
                                    </Tag>
                                )}
                            </div>
                        </Card>
                    </Col>                    {/* RIGHT COLUMN: GM & PlaySpace & Organizer Info */}
                    <Col xs={24} lg={8}>
                        {/* GAME MASTER CARD */}
                        <Card style={{ ...launcherCard, marginBottom: 24 }} title={<Title level={4} style={{ margin: 0 }}><UserOutlined /> {t('invitations.detail.gmTitle', 'Game Master')}</Title>}>
                            <Flex align="center" gap="middle" style={{ marginBottom: 16 }}>
                                <Avatar
                                    src={detail.gmPicture}
                                    icon={<UserOutlined />}
                                    size={56}
                                />
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>
                                        {detail.gmName || 'Anonymous GM'}
                                    </Title>
                                    {detail.gmNickname && (
                                        <Text type="secondary">@{detail.gmNickname}</Text>
                                    )}
                                </div>
                            </Flex>

                            <Divider style={{ margin: '14px 0' }} />

                            {/* PLAYER CAPACITY BREAKDOWN */}
                            <div>
                                <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                                    <Text strong style={{ fontSize: 13 }}>
                                        <TeamOutlined style={{ marginRight: 6 }} />
                                        {t('invitations.detail.playerCapacity', 'Player Capacity')}
                                    </Text>
                                    <Tag
                                        color={isAtMaximum ? 'red' : isOverMinimum ? 'green' : 'gold'}
                                        style={{ margin: 0, fontWeight: 'bold' }}
                                    >
                                        {isAtMaximum
                                            ? t('invitations.detail.capacityFull', 'Table Full')
                                            : isOverMinimum
                                                ? t('invitations.detail.minimumMet', 'Min Reached')
                                                : t('invitations.detail.belowMinimum', 'Below Min')}
                                    </Tag>
                                </Flex>

                                <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 14px' }}>
                                    <Flex justify="space-between" style={{ marginBottom: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('invitations.detail.verifiedPlayers', 'Confirmed / Joined')}:
                                        </Text>
                                        <Text strong style={{ fontSize: 13 }}>
                                            {verifiedPlayers} {t('invitations.detail.playersSuffix', 'players')}
                                        </Text>
                                    </Flex>

                                    <Flex justify="space-between" style={{ marginBottom: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('invitations.detail.minPlayers', 'Minimum Required')}:
                                        </Text>
                                        <Text style={{ fontSize: 13 }}>
                                            {detail.minTotalPlayers} {t('invitations.detail.playersSuffix', 'players')}
                                        </Text>
                                    </Flex>

                                    <Flex justify="space-between" style={{ marginBottom: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('invitations.detail.maxPlayers', 'Maximum Allowed')}:
                                        </Text>
                                        <Text style={{ fontSize: 13 }}>
                                            {detail.maxTotalPlayers} {t('invitations.detail.playersSuffix', 'players')}
                                        </Text>
                                    </Flex>

                                    {detail.preexistingPlayers > 0 && (
                                        <Flex justify="space-between">
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {t('invitations.detail.preexistingPlayers', 'Pre-existing')}:
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {detail.preexistingPlayers} {t('invitations.detail.playersSuffix', 'players')}
                                            </Text>
                                        </Flex>
                                    )}

                                    <Progress
                                        percent={Math.min(100, Math.round((verifiedPlayers / detail.maxTotalPlayers) * 100))}
                                        status={isAtMaximum ? 'exception' : isOverMinimum ? 'success' : 'normal'}
                                        strokeColor={isAtMaximum ? '#ff4d4f' : isOverMinimum ? '#52c41a' : '#faad14'}
                                        style={{ marginTop: 8 }}
                                    />

                                    <div style={{ marginTop: 6, fontSize: 12 }}>
                                        {isAtMaximum ? (
                                            <Text type="danger">
                                                <CheckCircleOutlined /> {t('invitations.detail.tableFullDesc', 'Maximum capacity reached. New requests go to waitlist.')}
                                            </Text>
                                        ) : isOverMinimum ? (
                                            <Text style={{ color: '#389e0d' }}>
                                                <CheckCircleOutlined /> {t('invitations.detail.minMetDesc', 'Minimum players reached! Adventure is ready to run.')}
                                            </Text>
                                        ) : (
                                            <Text type="warning">
                                                <InfoCircleOutlined /> {t('invitations.detail.needMorePlayers', 'Needs {{count}} more to reach minimum.', { count: detail.minTotalPlayers - verifiedPlayers })}
                                            </Text>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* PLAY SPACE & AMENITIES CARD */}
                        <Card style={{ ...launcherCard, marginBottom: 24 }} title={<Title level={4} style={{ margin: 0 }}>{detail.online ? <GlobalOutlined /> : <EnvironmentOutlined />} {t('invitations.detail.playSpaceTitle', 'Play Space')}</Title>}>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                    {detail.online ? t('invitations.detail.platform', 'Online Platform') : t('invitations.detail.address', 'Location Address')}
                                </Text>
                                {detail.online ? (
                                    <Tag color="cyan" icon={<GlobalOutlined />} style={{ fontSize: 13 }}>
                                        {detail.onlinePlatform || 'Online'}
                                    </Tag>
                                ) : (
                                    <div>
                                        <Paragraph style={{ margin: '4px 0' }}>
                                            {detail.addressGmapsFormatted || t('invitations.detail.locationProvidedOnBooking', 'Location details provided upon reservation.')}
                                        </Paragraph>
                                        {detail.addressGmapsUrl && (
                                            <a href={detail.addressGmapsUrl} target="_blank" rel="noopener noreferrer">
                                                <EnvironmentOutlined /> {t('invitations.detail.openGmaps', 'View on Google Maps')}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>

                            {detail.accessInfo && (
                                <div style={{ marginBottom: 16 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                        {t('invitations.detail.accessInfo', 'Access Information')}
                                    </Text>
                                    <Paragraph type="secondary" style={{ fontSize: 13 }}>
                                        {detail.accessInfo}
                                    </Paragraph>
                                </div>
                            )}

                            {/* AMENITIES */}
                            <Divider style={{ margin: '12px 0' }} />
                            <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                                {t('invitations.detail.amenities', 'Space Features & Rules')}
                            </Title>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <AmenityItem label={t('invitations.amenities.wheelchair', 'Wheelchair Accessible')} active={detail.wheelchairAccess} />
                                <AmenityItem label={t('invitations.amenities.canEatIn', 'Food Allowed')} active={detail.canEatIn} />
                                <AmenityItem label={t('invitations.amenities.canDrinkIn', 'Drinks Allowed')} active={detail.canDrinkIn} />
                                <AmenityItem label={t('invitations.amenities.canSmoke', 'Smoking Allowed')} active={detail.canSmoke} />
                                <AmenityItem label={t('invitations.amenities.publicSpace', 'Public Space')} active={detail.publicSpace} />
                            </Space>
                        </Card>

                        {/* ORGANIZER CARD */}
                        {detail.organizerName && (
                            <Card style={{ ...launcherCard }} title={<Title level={4} style={{ margin: 0 }}><ReadOutlined /> {t('invitations.detail.organizerTitle', 'Organized By')}</Title>}>
                                <Flex align="center" gap="small" style={{ marginBottom: 12 }}>
                                    {detail.organizerLogoUrl && (
                                        <Avatar src={detail.organizerLogoUrl} size="large" />
                                    )}
                                    <Title level={5} style={{ margin: 0 }}>{detail.organizerName}</Title>
                                </Flex>
                                {detail.organizerDescription && (
                                    <Paragraph type="secondary" style={{ fontSize: 13, margin: 0 }}>
                                        {detail.organizerDescription}
                                    </Paragraph>
                                )}
                            </Card>
                        )}
                    </Col>
                </Row>

                {/* RESERVATION CALL TO ACTION / ORGANIZER JOIN REQUESTS LINK SECTION */}
                {isOrganizer ? (
                    <Card style={{ ...launcherCard, marginTop: 24, padding: '20px 24px', textAlign: 'center' }}>
                        <Flex vertical align="center" gap="middle">
                            <Title level={4} style={{ margin: 0 }}>
                                <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                {t('organizer.joinRequests.heading', 'Join Requests')}
                            </Title>
                            <Paragraph style={{ margin: 0, color: '#666', fontSize: 14 }}>
                                {t('organizer.invitations.organizerCtaDesc', 'You are organizing this adventure. View and manage player join requests and RSVPs.')}
                            </Paragraph>
                            <Button
                                type="primary"
                                size="large"
                                icon={<TeamOutlined />}
                                onClick={() => navigate(`/organizer/adventures/${detail.adventureId}/join-requests`)}
                                style={{ minWidth: 220 }}
                            >
                                {t('organizer.joinRequests.title', 'Join Requests')}
                            </Button>
                        </Flex>
                    </Card>
                ) : (
                    <Card style={{ ...launcherCard, marginTop: 24, padding: '12px 24px' }}>
                        <ReservationCtaSection
                            canAcceptReservations={true}
                            hasUserAlreadyReserved={hasUserAlreadyReserved}
                            isHostOrGm={isGm}
                            hostOrGmContent={
                                <Alert
                                    type="info"
                                    showIcon
                                    message={t('reservation.cta.hostOrGmTitle', 'Adventure Host / Game Master')}
                                    description={t('reservation.cta.hostOrGm', 'You are the Game Master of this adventure, so you cannot reserve a seat as a player.')}
                                />
                            }
                            alreadyReservedContent={
                                userJoinRequest && (
                                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                                        <div style={{ marginBottom: 12 }}>
                                            {userJoinRequest.status === 'APPROVED' && (
                                                <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>
                                                    {t('joinRequests.status.approved', 'Reservation Confirmed')}
                                                </Tag>
                                            )}
                                            {userJoinRequest.status === 'PENDING' && (
                                                <Tag color="gold" icon={<ClockCircleOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>
                                                    {t('joinRequests.status.pending', 'Pending GM Approval')}
                                                </Tag>
                                            )}
                                            {userJoinRequest.status === 'REJECTED' && (
                                                <Tag color="red" icon={<CloseCircleOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>
                                                    {t('joinRequests.status.rejected', 'Request Declined')}
                                                </Tag>
                                            )}
                                        </div>
                                        <Paragraph style={{ fontSize: 14, marginBottom: 16 }}>
                                            {userJoinRequest.status === 'APPROVED'
                                                ? t('invitations.detail.alreadyReservedApproved', 'You have a confirmed reservation for this adventure.')
                                                : userJoinRequest.status === 'PENDING'
                                                    ? t('invitations.detail.alreadyReservedPending', 'You have submitted a join request that is awaiting review.')
                                                    : t('invitations.detail.alreadyReservedRejected', 'Your previous join request was not accepted.')}
                                        </Paragraph>
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<EyeOutlined />}
                                            onClick={() => navigate(`/join-requests/${userJoinRequest.id}`)}
                                            style={{ minWidth: 220 }}
                                        >
                                            {t('invitations.detail.viewJoinRequest', 'View Your Join Request')}
                                        </Button>
                                    </div>
                                )
                            }
                        >
                            {!showBookingForm ? (
                                <div>
                                    <Label strong trl={'reservation.buttons.reserveSeat'} />
                                    <div style={{ marginTop: 12 }}>
                                        <Button
                                            type="primary"
                                            size="large"
                                            style={{ width: '100%', backgroundColor: 'limegreen', borderColor: 'limegreen' }}
                                            onClick={() => setShowBookingForm(true)}
                                        >
                                            {t('reservation.buttons.reserveSeat', 'Reserve a seat')}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <SessionRsvpBookingForm
                                    invitation={detail}
                                    onSuccess={(jr) => {
                                        refetchUserJoinRequest();
                                        navigate(`/join-requests/${jr.id}`);
                                    }}
                                    onCancel={() => setShowBookingForm(false)}
                                />
                            )}
                        </ReservationCtaSection>
                    </Card>
                )}
            </div>
        </LauncherLayout>
    );
};

const AmenityItem: React.FC<{ label: string; active: boolean }> = ({ label, active }) => (
    <Flex justify="space-between" align="center" style={{ fontSize: 13 }}>
        <span>{label}</span>
        {active ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
        ) : (
            <Tag color="default" icon={<CloseCircleOutlined />}>No</Tag>
        )}
    </Flex>
);
